import { Map, Plane, Calendar, CalendarCheck, CalendarClock, Clapperboard, Users, Warehouse, FileText, CreditCard, TrendingUp, BarChart3, DollarSign, Settings, MapPin, Shield, Plug, AlertTriangle, Fuel, Zap, Activity, Layers, Hammer, type LucideIcon } from 'lucide-react'

/**
 * Scope indicates where a nav item is available:
 * - 'org': Only in org-wide views (e.g., /clients, /dashboard)
 * - 'location': Only in location-scoped views (e.g., /kdet/hangar-view)
 * - 'both': Available in both contexts
 */
export type NavScope = 'org' | 'location' | 'both'

export interface NavItem {
  id: string
  label: string
  icon: LucideIcon
  /**
   * Path template. Use '{loc}' as placeholder for location slug.
   * @example '/clients' for org-wide
   * @example '{loc}/ramp' for location-scoped
   */
  pathTemplate: string
  /** Scope where this item is visible */
  scope: NavScope
  implemented: boolean
  /** Optional query string to append */
  query?: string
  /** Optional subtitle/description shown below the label */
  description?: string
}

export interface NavSection {
  id: string
  label: string
  icon: LucideIcon
  items: NavItem[]
  /** Scope where this section is visible (derived from items if not set) */
  scope?: NavScope
  defaultOpen?: boolean
}

/**
 * Resolve a path template with the current location slug.
 * @param template Path template (e.g., '{loc}/ramp' or '/clients')
 * @param locationSlug Current location slug (e.g., 'kdet')
 * @returns Resolved path (e.g., '/kdet/hangar-view' or '/clients')
 */
export function resolveNavPath(template: string, locationSlug?: string | null): string {
  if (!template.includes('{loc}')) {
    // Org-wide path - return as-is (ensure leading slash)
    return template.startsWith('/') ? template : `/${template}`
  }

  if (!locationSlug) {
    // Location-scoped but no location selected - return template with placeholder
    // This shouldn't happen in practice, but provides a fallback
    console.warn('resolveNavPath: Location-scoped path without location slug')
    return template.replace('{loc}', 'dashboard')
  }

  // Replace {loc} with actual slug
  const path = template.replace('{loc}', locationSlug)
  return path.startsWith('/') ? path : `/${path}`
}

// ============================================
// Sidebar Navigation (v2 — 3-panel layout)
// ============================================

/**
 * Menu section for the sidebar. Unlike NavSection, label is optional
 * (hero items and bottom items have no section header label).
 */
export interface SidebarMenuSection {
  id: string
  /** Section header label (e.g., "OPERATIONS"). Omit for hero/bottom groups. */
  label?: string
  items: NavItem[]
}

/** Hero item for location mode — Ramp View */
export const locationHeroItem: NavItem = {
  id: 'ramp-view',
  label: 'Ramp View',
  icon: Map,
  pathTemplate: '{loc}/ramp',
  scope: 'location',
  implemented: true,
}

/** Secondary hero item for location mode — Masters Event Planner */
export const locationHeroItemSecondary: NavItem = {
  id: 'event-planner',
  label: 'Event Planner',
  icon: CalendarClock,
  pathTemplate: '{loc}/event-planner',
  scope: 'location',
  implemented: true,
}

/** Quick-access entry for location mode — Cinematic Studio (promo playground) */
export const locationHeroItemCinematic: NavItem = {
  id: 'cinematic',
  label: 'Cinematic Studio',
  icon: Clapperboard,
  pathTemplate: '{loc}/cinematic',
  scope: 'location',
  implemented: true,
}

/** Quick-access entry for location mode — Location Designer (builder) */
export const locationHeroItemDesigner: NavItem = {
  id: 'layout-designer',
  label: 'Location Designer',
  icon: Hammer,
  pathTemplate: '{loc}/builder',
  scope: 'location',
  implemented: true,
}

/** Hero item for network/org mode — Dashboard */
export const networkHeroItem: NavItem = {
  id: 'network-dashboard',
  label: 'Dashboard',
  icon: Activity,
  pathTemplate: '/dashboard',
  scope: 'org',
  implemented: true,
}

/** Location-scoped menu sections (when viewing a specific location) */
export const locationMenu: SidebarMenuSection[] = [
  {
    id: 'quick-access',
    label: 'QUICK ACCESS',
    items: [
      locationHeroItem,
      locationHeroItemSecondary,
      locationHeroItemCinematic,
      locationHeroItemDesigner,
    ],
  },
  {
    id: 'operations',
    label: 'OPERATIONS',
    items: [
      { id: 'quick-fit', label: 'Quick Fit', icon: Zap, pathTemplate: '{loc}/quick-fit', scope: 'location', implemented: false },
      { id: 'todays-board', label: "Today's Board", icon: Calendar, pathTemplate: '{loc}/schedule', scope: 'location', implemented: true },
      { id: 'transient-stays', label: 'Transient Stays', icon: CalendarCheck, pathTemplate: '{loc}/transients', scope: 'location', implemented: true },
      { id: 'fuel-tracking', label: 'Fuel Records', icon: Fuel, pathTemplate: '{loc}/fuel', scope: 'location', implemented: true },
      { id: 'alerts', label: 'Alerts', icon: AlertTriangle, pathTemplate: '{loc}/conflicts', scope: 'location', implemented: true },
    ],
  },
  {
    id: 'directory',
    label: 'DIRECTORY',
    items: [
      { id: 'clients', label: 'Clients', icon: Users, pathTemplate: '{loc}/clients', scope: 'location', implemented: true },
      { id: 'aircrafts', label: 'Aircrafts', icon: Plane, pathTemplate: '{loc}/aircrafts', scope: 'location', implemented: true },
      { id: 'leases', label: 'Leases', icon: FileText, pathTemplate: '{loc}/leases', scope: 'location', implemented: true },
      { id: 'spaces', label: 'Spaces', icon: Warehouse, pathTemplate: '{loc}/space', scope: 'location', implemented: true },
    ],
  },
  {
    id: 'reports',
    label: 'Reports',
    items: [
      { id: 'space-yield', label: 'Space Yield', icon: TrendingUp, pathTemplate: '{loc}/reports/space-yield', scope: 'location', implemented: true },
      { id: 'billing', label: 'Billing', icon: CreditCard, pathTemplate: '{loc}/invoices', scope: 'location', implemented: false },
    ],
  },
]

/** Bottom items for location mode (shown below divider, no section label) */
export const locationBottomItems: NavItem[] = [
  { id: 'loc-integrations', label: 'Integrations', icon: Plug, pathTemplate: '{loc}/settings/integrations', scope: 'location', implemented: true },
  { id: 'base-settings', label: 'Settings', icon: Settings, pathTemplate: '{loc}/settings', scope: 'location', implemented: true },
]

/** Network/org-wide menu sections (when viewing all locations) */
export const networkMenu: SidebarMenuSection[] = [
  {
    id: 'portfolio',
    label: 'PORTFOLIO',
    items: [
      { id: 'locations', label: 'Locations', icon: MapPin, pathTemplate: '/locations', scope: 'org', implemented: true },
    ],
  },
  {
    id: 'directory',
    label: 'DIRECTORY',
    items: [
      { id: 'all-aircraft', label: 'All Aircraft', icon: Plane, pathTemplate: '/aircrafts', scope: 'org', implemented: true },
      { id: 'all-clients', label: 'All Clients', icon: Users, pathTemplate: '/clients', scope: 'org', implemented: true },
      { id: 'all-contracts', label: 'All Contracts', icon: FileText, pathTemplate: '/leases', scope: 'org', implemented: true },
    ],
  },
  {
    id: 'analytics',
    label: 'ANALYTICS',
    items: [
      { id: 'space-yield', label: 'Space Yield', icon: BarChart3, pathTemplate: '/reports/space-yield', scope: 'org', implemented: true },
      { id: 'occupancy-reports', label: 'Occupancy Reports', icon: TrendingUp, pathTemplate: '/reports/occupancy', scope: 'org', implemented: false },
      { id: 'revenue-analysis', label: 'Revenue Analysis', icon: DollarSign, pathTemplate: '/reports/revenue', scope: 'org', implemented: false },
      { id: 'capacity-planning', label: 'Capacity Planning', icon: Layers, pathTemplate: '/reports/capacity', scope: 'org', implemented: false },
    ],
  },
]

/** Bottom items for network mode (shown below divider, no section label) */
export const networkBottomItems: NavItem[] = [
  { id: 'user-management', label: 'User Management', icon: Shield, pathTemplate: '/settings/users', scope: 'org', implemented: true },
  { id: 'integrations', label: 'Integrations', icon: Plug, pathTemplate: '/settings/integrations', scope: 'org', implemented: true },
  { id: 'network-settings', label: 'Network Settings', icon: Settings, pathTemplate: '/settings', scope: 'org', implemented: true },
]

/**
 * Get the dashboard path based on current scope.
 */
export function getDashboardPath(locationSlug?: string | null): string {
  if (locationSlug) {
    return `/${locationSlug}/dashboard`
  }
  return '/dashboard'
}
